/**
 * BrushEntity - 브러시 도형 Entity
 */
import { SHAPES } from '../../../const'
import type { Vector2d } from '../../../types'
import type { HitArea } from '../../../backend/types'
import type { IGroup, IRenderer } from '../../../backend/IRenderer'

/**
 * Brush 라인 데이터
 */
export interface BrushLineData {
  points: number[]
  strokeWidth: number
  stroke: string
}

/**
 * Brush 생성 설정
 */
export interface BrushCreateConfig {
  color: string
  width?: number
}

/**
 * Brush 초기화 설정
 */
export interface BrushInitConfig {
  id?: string
  createPoint: Vector2d
  balloonPoint?: Vector2d
  config: BrushCreateConfig
  points?: number[]
  rotation?: number
  layerId?: string
  brushLines?: BrushLineData[]
  interactionMode?: boolean
}

// 직선/사각형 스트로크 여부 (직선: 2점, 사각형: 시작·끝이 일치하는 닫힌 5점 폴리라인)
export function isShapeBrushPoints(points: number[]): boolean {
  if (points.length === 4) return true
  return points.length === 10 && points[0] === points[8] && points[1] === points[9]
}

/**
 * Brush 도형 생성 함수
 * @param initConfig - 초기화 설정
 * @returns Brush 관련 노드 핸들
 */
export function createBrushShape(
  renderer: IRenderer,
  initConfig: BrushInitConfig,
): {
  brush: IGroup
  color: string
} {
  const { config, brushLines } = initConfig
  const color = config.color
  const strokeWidth = config.width || 1

  // 브러시 그룹 생성(획 Line만 담는다 — 연속 입력·지우개가 추가하는 Line도 이 그룹에 들어가 기하 hit 대상이 됨)
  const brush = renderer.createGroup({
    x: 0,
    y: 0,
    draggable: false,
    // 브러시 획은 hit 캔버스 대신 기하 판정(선분 거리)으로 선택한다(KonvaGeomHit)
    hitCanvas: false,
  })

  // 히트 영역: 선 점들을 직선으로 이은 경로를 생성 시점 굵기 ×2로 stroke
  // (실제 hit 굵기는 hitStrokeWidth가 정한다 — entityFactory가 +50 지정, backend/konva/KonvaNode applyHitArea 주석)
  const createHitArea = (lineStrokeWidth: number): HitArea => ({
    kind: 'strokeScale',
    scale: 2,
    baseStrokeWidth: lineStrokeWidth,
  })

  if (brushLines && brushLines.length > 0) {
    // brushLines 데이터로부터 Line 생성
    for (const lineData of brushLines) {
      // 직선/사각형 스트로크는 곡선 보간 없이 각진 끝/모서리로 그림
      const isShapeStroke = isShapeBrushPoints(lineData.points)
      const line = renderer.createLine({
        points: lineData.points,
        stroke: lineData.stroke || color,
        strokeWidth: lineData.strokeWidth || strokeWidth,
        lineCap: isShapeStroke ? 'butt' : 'round',
        lineJoin: isShapeStroke ? 'miter' : 'round',
        tension: isShapeStroke ? 0 : 0.5,
        // 사각형은 닫아서 시작/끝 모서리도 miter로 각지게
        closed: isShapeStroke && lineData.points.length === 10,
        listening: true,
        hitArea: createHitArea(lineData.strokeWidth || strokeWidth),
      })
      brush.add(line)
    }
  } else if (initConfig.points && initConfig.points.length > 0) {
    // 기존 points 데이터로부터 Line 생성
    // 직선/사각형 스트로크는 곡선 보간 없이 각진 끝/모서리로 그림
    const isShapeStroke = isShapeBrushPoints(initConfig.points)
    const line = renderer.createLine({
      points: initConfig.points,
      stroke: color,
      strokeWidth: strokeWidth,
      lineCap: isShapeStroke ? 'butt' : 'round',
      lineJoin: isShapeStroke ? 'miter' : 'round',
      tension: isShapeStroke ? 0 : 0.5,
      // 사각형은 닫아서 시작/끝 모서리도 miter로 각지게
      closed: isShapeStroke && initConfig.points.length === 10,
      listening: true,
      hitArea: createHitArea(strokeWidth),
    })
    brush.add(line)
  }

  return {
    brush,
    color,
  }
}

/**
 * Brush Entity export 데이터
 */
export interface BrushExportData {
  type: typeof SHAPES.BRUSH
  shapeId: string
  point: Vector2d
  balloonPoint: Vector2d
  color: string
  layerId: string
  points?: number[]
  width?: number
  rotate?: number
  brushLines?: Array<{ points: number[]; strokeWidth: number }>
}

/**
 * Brush Entity를 export 데이터로 변환
 */
export function exportBrushData(entity: {
  id: string
  position: Vector2d
  balloonPosition: Vector2d
  color: string
  layerId: string
  points?: number[]
  strokeWidth?: number
  rotation?: number
  brushLines?: BrushLineData[]
}): BrushExportData {
  return {
    type: SHAPES.BRUSH,
    shapeId: entity.id,
    point: entity.position,
    balloonPoint: entity.balloonPosition,
    color: entity.color,
    layerId: entity.layerId,
    points: entity.points,
    width: entity.strokeWidth,
    rotate: entity.rotation,
    brushLines: entity.brushLines?.map(line => ({
      points: line.points,
      strokeWidth: line.strokeWidth,
    })),
  }
}
